import {useContext} from 'react';

import {useTranslation} from 'react-i18next';

import {Box, Typography} from '@mui/material';

import {WorkforceCulturalPillarScoreRow} from './WorkforceCulturalPillarScoreRow';
import {culturalAlignmentColors} from './WorkforceProfileDetail.colors';
import {LanguageContext} from '../../../../../contexts/LanguageContext/LanguageContext';
import {alignmentStatus, orangeColors} from '../../../../../layouts/theme/designSystem';
import {WorkforceProfileCulturalFitPillarScore} from '../../types/WorkforceProfileTypes';
import {COMPANY_MARKER_COLOR, PROFILE_MARKER_COLOR} from '../../utils/WorkforceProfileDetail/workforceCulturalAlignmentUtils';

const pillarDeltaStyles = {
  aligned: {bgcolor: alignmentStatus.metChipBg, color: alignmentStatus.metChipFg},
  above: {
    bgcolor: culturalAlignmentColors.badge.moderate.bgcolor,
    color: culturalAlignmentColors.badge.moderate.color,
  },
  below: {
    bgcolor: culturalAlignmentColors.badge.different.bgcolor,
    color: culturalAlignmentColors.badge.different.color,
  },
};

export interface Props {
  pillar: WorkforceProfileCulturalFitPillarScore;
}

export const WorkforceCulturalPillarCard = ({pillar}: Props) => {
  const {t} = useTranslation('translation', {keyPrefix: 'workforceProfilesPage.detail.culturalFitAnalysis'});
  const currentLanguage = useContext(LanguageContext);
  const delta = pillar.workforceProfileScore - pillar.companyScore;
  const roundedDelta = Math.round(delta * 10) / 10;

  let deltaLabel: string | null = null;
  let deltaStyle = pillarDeltaStyles.above;

  if (Math.abs(roundedDelta) < 0.05) {
    deltaLabel = t('pillarDeltaAligned');
    deltaStyle = pillarDeltaStyles.aligned;
  } else if (roundedDelta > 0) {
    deltaLabel = t('pillarDeltaAbove', {value: roundedDelta.toFixed(1)});
  } else {
    deltaLabel = t('pillarDeltaBelow', {value: Math.abs(roundedDelta).toFixed(1)});
    deltaStyle = pillarDeltaStyles.below;
  }

  return (
    <Box
      sx={{
        p: '1.25rem 1.5rem',
        bgcolor: 'background.paper',
        border: `0.5px solid ${orangeColors[3]}`,
        borderRadius: '12px',
      }}
    >
      <Typography sx={{fontSize: 13, fontWeight: 500, color: 'text.primary', mb: 1.5}}>
        {pillar.title[currentLanguage]}
      </Typography>
      <WorkforceCulturalPillarScoreRow
        label={t('companyLabel')}
        score={pillar.companyScore}
        color={COMPANY_MARKER_COLOR}
      />
      <WorkforceCulturalPillarScoreRow
        label={t('profileLabel')}
        score={pillar.workforceProfileScore}
        color={PROFILE_MARKER_COLOR}
      />
      {deltaLabel && (
        <Box
          component='span'
          sx={{
            display: 'inline-block',
            mt: 1.25,
            fontSize: 11,
            px: 1,
            py: '3px',
            borderRadius: '10px',
            bgcolor: deltaStyle.bgcolor,
            color: deltaStyle.color,
          }}
        >
          {deltaLabel}
        </Box>
      )}
    </Box>
  );
};
